import { Formik } from "formik";
import omit from "lodash/omit";
import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { createContract } from "../../../../../../../../actions/employers/contractActions";
import { getTempPrerequisites } from "../../../../../../../../actions/employers/tempPrerequisiteActions";
import { setProfileDrawerVisible } from "../../../../../../../../actions/shared/uiActions";
import { getJobCast } from "../../../../../../../../selectors/jobcastSelectors";
import {
  buildFullName,
  mapKeysToSnakeCase,
} from "../../../../../../../../util/formatHelpers";
import { string, object, array } from "../../../../../../../../util/yup";
import useSnackbar from "../../../../../../../General/customHooks/useSnackbar";
import { mergePrerequisites } from "../../../../../helpers";
import { getCurrentOrganization } from "../../../../../../../../selectors/organizationSelectors";
import { getCurrentIndividual } from "../../../../../../../../selectors/individualSelectors";

function FormInitializer({ candidate, children }) {
  const dispatch = useDispatch();
  const snackbar = useSnackbar();
  const currentOrganization = useSelector(getCurrentOrganization);
  const currentIndividual = useSelector(getCurrentIndividual);
  const organizationPrerequisites = useSelector(getTempPrerequisites);
  const jobCast = useSelector((state) =>
    getJobCast(state, candidate.attributes.jobcastId)
  );
  const jobcastPrerequisites = jobCast.attributes.tempPrerequisites;

  const costCenters = useSelector((state) => state.costCenters.items);

  const initialValues = useMemo(() => {
    const {
      agencyName,
      agencyLogoUrl,
      recruiterFirstName,
      recruiterLastName,
      hourlyBillRate,
      availableStartDate,
      defaultTimesheetSubmissionParty,
    } = candidate.attributes;

    const costCenterIds = Object.keys(costCenters);
    return {
      // AGENCY INFORMATION (uneditable fields)
      agencyName,
      agencyLogoUrl,
      recruiterName: buildFullName(recruiterFirstName, recruiterLastName),
      hourlyBillRate,
      // WORK INFORMATION
      scheduledStartDate: availableStartDate,
      scheduledEndDate: null,
      primaryWorkState: null,
      primaryWorkZipCode: null,
      timesheetSubmissionParty:
        defaultTimesheetSubmissionParty ||
        currentOrganization.defaultTimesheetSubmissionParty,
      statementOfWorkDescription: jobCast.attributes.description.replace(
        /<img[^>]*>/g,
        ""
      ),
      temp_prerequisites: mergePrerequisites(
        jobcastPrerequisites,
        organizationPrerequisites
      ),
      sosSignerIndividualId: currentIndividual.id,
      // BILLING INFORMATION
      costCenterId: jobCast.attributes.costCenterId || (costCenterIds.length === 1 ? costCenterIds[0] : null),
      invoiceMemo: null,
      poNumber: null,
      contractClientIndividualIds:
        jobCast.attributes.contractClientIndividualIds || [],
    };
  }, [
    candidate.attributes,
    costCenters,
    jobcastPrerequisites,
    organizationPrerequisites,
    jobCast.attributes.costCenterId,
    jobCast.attributes.contractClientIndividualIds,
    jobCast.attributes.description,
  ]);

  const validationSchema = useMemo(
    () => {
      const poNumberValidation = currentOrganization.requirePoNumber
        ? string().nullable().required("Cannot be blank").min(4, "Must be greater than 3 characters")
        : string().nullable();

      return object().shape({
        scheduledStartDate: string().nullable().required("Cannot be blank"),
        scheduledEndDate: string().nullable(),
        primaryWorkState: string().nullable().required("Cannot be blank"),
        primaryWorkZipCode: string()
          .nullable()
          .required("Cannot be blank")
          .matches(/^\d*$/, "Digits only")
          .min(5, "Must be 5 characters")
          .max(5, "Must be 5 characters"),
        timesheetSubmissionParty: string()
          .nullable()
          .required("Cannot be blank")
          .oneOf(["client", "supplier"]),
        statementOfWorkDescription: string()
          .nullable()
          .minWordsWithoutHTML(3, "Must be at least 3 words")
          .required("Cannot be blank"),
        costCenterId: string().nullable().required("Cannot be blank"),
        invoiceMemo: string()
          .nullable()
          .max(600, "Must be less than 600 characters"),
        poNumber: poNumberValidation,
        contractClientIndividualIds: array()
          .of(string())
          .min(1, "Cannot be blank"),
      });
    },
    [currentOrganization]
  );

  const onSubmit = useCallback(
    (values, { setSubmitting }) => {
      const submitValues = {
        ...mapKeysToSnakeCase(
          omit(values, [
            "agencyName",
            "agencyLogoUrl",
            "recruiterName",
            "hourlyBillRate",
          ])
        ),
        temp_prerequisites: values.temp_prerequisites.filter(
          (prerequisite) => prerequisite.checked
        ),
        recruiter_submission_id: candidate.id,
      };

      return dispatch(createContract(submitValues))
        .then(() => {
          setSubmitting(false);
          dispatch(setProfileDrawerVisible(false));
          snackbar.showMessage(
            "Candidate successfully advanced",
            null,
            () => {},
            { duration: 3000 }
          );
          // open rate modal
        })
        .catch(() => {
          setSubmitting(false);
        });
    },
    [dispatch, candidate.id, snackbar]
  );

  return (
    <Formik {...{ initialValues, validationSchema, onSubmit }}>
      {children}
    </Formik>
  );
}

export default FormInitializer;
